import {
  ACCENT_KEYS,
  type AccentKey,
  BRIGHT_KEYS,
  BRIGHT_SOURCE,
  NEUTRAL_KEYS,
  type NeutralKey,
  PALETTE_KEYS,
  type PaletteKey,
} from "@/model/palette";
import type { Variant } from "@/model/tokens";
import { contrastRatio, fixContrast } from "./contrast";
import { clamp, fromLch, type Hex, type Lch, toLch, wrapHue } from "./oklch";

export type Palette = Record<PaletteKey, Hex>;

/** Hue (OKLCH degrees) that reads as each accent's name. */
export const SEMANTIC_HUES: Record<AccentKey, number> = {
  red: 25,
  orange: 55,
  yellow: 90,
  green: 135,
  cyan: 195,
  blue: 255,
  purple: 305,
  pink: 350,
};

export type Harmony = "natural" | "analogous" | "complementary" | "triadic";
export type Mood = "balanced" | "muted" | "vivid" | "pastel";

export const HARMONIES: { id: Harmony; label: string; hint: string }[] = [
  { id: "natural", label: "Natural", hint: "Each accent keeps its usual hue" },
  { id: "analogous", label: "Analogous", hint: "Accent hues lean toward the seed" },
  { id: "complementary", label: "Complementary", hint: "Seed and its opposite are emphasized" },
  { id: "triadic", label: "Triadic", hint: "Three hues 120° apart are emphasized" },
];

export const MOODS: { id: Mood; label: string }[] = [
  { id: "balanced", label: "Balanced" },
  { id: "muted", label: "Muted" },
  { id: "vivid", label: "Vivid" },
  { id: "pastel", label: "Pastel" },
];

const MOOD_LC: Record<Mood, Record<Variant, { l: number; c: number }>> = {
  balanced: { dark: { l: 0.74, c: 0.12 }, light: { l: 0.55, c: 0.13 } },
  muted: { dark: { l: 0.72, c: 0.07 }, light: { l: 0.52, c: 0.08 } },
  vivid: { dark: { l: 0.72, c: 0.17 }, light: { l: 0.56, c: 0.18 } },
  pastel: { dark: { l: 0.84, c: 0.08 }, light: { l: 0.62, c: 0.09 } },
};

function hueDistance(a: number, b: number): number {
  const d = Math.abs(wrapHue(a) - wrapHue(b));
  return Math.min(d, 360 - d);
}

/** Moves hue `h` toward `target` along the shortest arc by fraction `t`. */
function leanHue(h: number, target: number, t: number): number {
  let d = wrapHue(target) - wrapHue(h);
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return wrapHue(h + d * t);
}

function emphasis(h: number, seedHue: number, harmony: Harmony): number {
  const near = (anchors: number[], width: number) =>
    anchors.some((a) => hueDistance(h, a) <= width) ? 1.15 : 0.75;
  switch (harmony) {
    case "complementary":
      return near([seedHue, seedHue + 180], 45);
    case "triadic":
      return near([seedHue, seedHue + 120, seedHue + 240], 40);
    default:
      return 1;
  }
}

/** Neutral ramp from base background to text, tinted toward `hue`. */
export function neutralRamp(opts: {
  variant: Variant;
  bgL?: number;
  fgL?: number;
  hue: number;
  chroma: number;
}): Record<NeutralKey, Hex> {
  const dark = opts.variant === "dark";
  const bgL = opts.bgL ?? (dark ? 0.23 : 0.97);
  const fgL = opts.fgL ?? (dark ? 0.9 : 0.36);
  const at = (t: number) => bgL + (fgL - bgL) * t;
  const tint = (l: number, cScale = 1): Hex =>
    fromLch({ l: clamp(l, 0, 1), c: opts.chroma * cScale, h: opts.hue });
  return {
    bg0: tint(dark ? bgL - 0.035 : bgL - 0.03),
    bg1: tint(bgL),
    bg2: tint(at(0.07)),
    bg3: tint(at(0.14), 1.2),
    bg4: tint(at(0.24)),
    fg3: tint(at(0.45)),
    fg2: tint(at(0.7)),
    fg1: tint(fgL, 0.8),
    fg0: tint(dark ? Math.min(1, fgL + 0.05) : Math.max(0, fgL - 0.06), 0.6),
  };
}

/** A complete palette from one seed color. */
export function paletteFromSeed(
  seed: Hex,
  opts: { variant: Variant; harmony?: Harmony; mood?: Mood; neutralTint?: number },
): Palette {
  const { variant } = opts;
  const harmony = opts.harmony ?? "natural";
  const mood = opts.mood ?? "balanced";
  const s = toLch(seed);
  const target = MOOD_LC[mood][variant];
  const neutrals = neutralRamp({
    variant,
    hue: s.h,
    chroma: (opts.neutralTint ?? 0.5) * 0.03,
  });
  const accents = {} as Record<AccentKey, Hex>;
  for (const key of ACCENT_KEYS) {
    let h = SEMANTIC_HUES[key];
    if (harmony === "analogous") h = leanHue(h, s.h, 0.35);
    const c = target.c * emphasis(h, s.h, harmony) * (key === "yellow" ? 0.95 : 1);
    // Yellow and green look dim at equal lightness: lift them slightly.
    const l = target.l + (key === "yellow" ? 0.06 : key === "green" ? 0.02 : 0);
    accents[key] = fromLch({ l, c, h });
  }
  // The seed itself replaces the accent whose hue it is closest to.
  const nearest = ACCENT_KEYS.reduce((best, key) =>
    hueDistance(SEMANTIC_HUES[key], s.h) < hueDistance(SEMANTIC_HUES[best], s.h) ? key : best,
  );
  if (s.c > 0.04) accents[nearest] = seed;
  // Keep every accent readable on the base background (a light seed in a light theme…).
  for (const key of ACCENT_KEYS) accents[key] = fixContrast(accents[key], neutrals.bg1, 3);
  const palette = { ...neutrals, ...accents } as Palette;
  return { ...palette, ...brightsFor(palette, variant) };
}

export function brightsFor(palette: Palette, variant: Variant, dl = 0.06): Partial<Palette> {
  const out: Partial<Palette> = {};
  for (const key of BRIGHT_KEYS) {
    const src = toLch(palette[BRIGHT_SOURCE[key]]);
    out[key] = fromLch({
      l: variant === "dark" ? src.l + dl : src.l - dl,
      c: src.c * 1.05,
      h: src.h,
    });
  }
  return out;
}

/** Equalizes OKLCH lightness and chroma across unlocked accents, keeping each hue. */
export function harmonizeAccents(palette: Palette, locks: PaletteKey[]): Palette {
  const keys = ACCENT_KEYS.filter((k) => !locks.includes(k));
  if (keys.length === 0) return palette;
  const lchs = keys.map((k) => toLch(palette[k]));
  const l = lchs.reduce((sum, x) => sum + x.l, 0) / lchs.length;
  const c = lchs.reduce((sum, x) => sum + x.c, 0) / lchs.length;
  const next = { ...palette };
  keys.forEach((k, i) => {
    const h = lchs[i]?.h ?? SEMANTIC_HUES[k];
    next[k] = fromLch({ l: l + (k === "yellow" ? 0.05 : 0), c, h });
  });
  return next;
}

/** Replaces unlocked swatches with `fresh`, leaving locked ones untouched. */
export function keepLocked(current: Palette, fresh: Palette, locks: PaletteKey[]): Palette {
  const next = { ...fresh };
  for (const k of locks) next[k] = current[k];
  return next;
}

export function randomPalette(variant: Variant, rand: () => number = Math.random): Palette {
  const pick = <T>(xs: readonly T[]): T => xs[Math.floor(rand() * xs.length)] as T;
  const seed = fromLch({
    l: variant === "dark" ? 0.72 : 0.55,
    c: 0.08 + rand() * 0.1,
    h: rand() * 360,
  });
  return paletteFromSeed(seed, {
    variant,
    harmony: pick(["natural", "natural", "analogous", "complementary", "triadic"] as const),
    mood: pick(["balanced", "balanced", "muted", "vivid", "pastel"] as const),
    neutralTint: rand(),
  });
}

export interface Adjustments {
  /** Degrees added to every hue. */
  hueShift: number;
  /** Multiplier for chroma (saturation). */
  chroma: number;
  /** Multiplier for the lightness distance between text/accents and the background. */
  contrast: number;
  /** −1 (cool) … 1 (warm): tints the neutrals. */
  warmth: number;
  /** Adds to the lightness of background swatches. */
  brightness: number;
}

export const NO_ADJUSTMENTS: Adjustments = {
  hueShift: 0,
  chroma: 1,
  contrast: 1,
  warmth: 0,
  brightness: 0,
};

const BG_KEYS: NeutralKey[] = ["bg0", "bg1", "bg2", "bg3", "bg4"];

/** Applies global adjustments to unlocked swatches of `base`. Pure: `base` is not modified. */
export function adjustPalette(base: Palette, adj: Adjustments, locks: PaletteKey[]): Palette {
  const bgL = toLch(base.bg1).l + adj.brightness;
  const next = { ...base };
  for (const key of PALETTE_KEYS) {
    if (locks.includes(key)) continue;
    const lch: Lch = toLch(base[key]);
    const isNeutral = (NEUTRAL_KEYS as readonly string[]).includes(key);
    const isBg = (BG_KEYS as string[]).includes(key);
    let { l, c, h } = lch;
    h = wrapHue(h + adj.hueShift);
    c *= adj.chroma;
    if (isBg) {
      l += adj.brightness;
    } else {
      l = bgL + (l - toLch(base.bg1).l) * adj.contrast;
    }
    if (isNeutral && adj.warmth !== 0) {
      const target = adj.warmth > 0 ? 70 : 250;
      const amount = Math.abs(adj.warmth);
      h = c < 0.005 ? target : leanHue(h, target, amount);
      c += 0.025 * amount;
    }
    next[key] = fromLch({ l, c, h });
  }
  return next;
}

/**
 * Turns a dark palette into a light one (or back): neutrals flip their lightness, accents
 * darken (or lighten) until they keep at least 3:1 against the new background.
 */
export function flipPalette(palette: Palette, from: Variant): Palette {
  const to: Variant = from === "dark" ? "light" : "dark";
  const map = (l: number) => (to === "light" ? 1.228 - 0.93 * l : (1.228 - l) / 0.93);
  const next = { ...palette };
  for (const key of NEUTRAL_KEYS) {
    if (key === "bg0") continue;
    const lch = toLch(palette[key]);
    next[key] = fromLch({ ...lch, l: clamp(map(lch.l), 0, 1) });
  }
  const base = toLch(next.bg1);
  next.bg0 = fromLch({ ...base, l: base.l - 0.03 });
  for (const key of ACCENT_KEYS) {
    const lch = toLch(palette[key]);
    const shifted = fromLch({ ...lch, l: to === "light" ? lch.l - 0.18 : lch.l + 0.18 });
    next[key] = contrastRatio(shifted, next.bg1) >= 3 ? shifted : fixContrast(shifted, next.bg1, 3);
  }
  return { ...next, ...brightsFor(next, to) };
}
